import { Version } from "../helpers/version";
import { StorageFactory } from "./storage";
import { AppUserModel, UserClaims } from "./users";
import { IUserStorageQuotaSummary } from "./media";

export type StandardRequest = {};
export type StandardResponse = {
  status?: boolean;
  timestamp?: number;
  message?: string;
  error?: string;
};

export type ApiRequest<T> = {};
export type ApiResponse<T> = {
  status?: boolean;
  timestamp?: number;
  message?: string;
  error?: string;
  result: T;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      HelloWorldReq:
 *        type: object
 *        required: [ "name" ]
 *        properties:
 *          name:
 *            type: string
 *            description: The name
 *          timestamp:
 *            type: number
 *            description: Timestamp
 *        example:
 *          name: TestHello
 *          timestamp: 1721049896625
 */
export type HelloWorldReq = StandardRequest & {
  name?: string;
  timestamp?: number;
};

/**
 * @swagger
 *  components:
 *    schemas:
 *      HelloWorldResponse:
 *        type: object
 *        required: [ "timestamp", "output" ]
 *        properties:
 *          timestamp:
 *            $ref: '#/components/schemas/TimestampParam'
 *          output:
 *            type: string
 *            description: The created name
 *          timeTaken:
 *            type: number
 *            description: The time taken in the call
 */
export type HelloWorldResponse = StandardResponse & {
  output?: string;
  timeTaken?: number;
};

export type AddUserClaimArgs = {
  email: string;
  password: string;
  claim: string;
};

export type RemoveUserClaimArgs = {
  email: string;
  claimName: string;
};

export type GetUserClaimsArgs = {
  email: string;
};
export type GetUserClaimsResponse = StandardResponse & {
  claims?: UserClaims | undefined;
};

export type GetStorageFactoryResponse = StandardResponse & {
  factory: StorageFactory;
};
export type CodeBundleResponse = {
  version: string;
  files: string[];
};

export type GetCodeBundlesRequest = StandardResponse & {
  limit?: number;
};
export type GetCodeBundlesResponse = StandardResponse & {
  bundles: CodeBundleResponse[];
};

export type UploadBundleFileArgs = {
  version: Version;
  filename: string;
  contents: ArrayBuffer;
};
export type UploadBundleFileRequest = StandardRequest & {
  version: string; // We cannot pass special objects to firebase - it cannot deserialise!
  filename: string;
  contents: string; // We should not pass BIG arrays to Firebase functions - they serialise as JSON maps - with position! So VERY inefficient
  base64: boolean;
};
export type UploadBundleFileResponse = StandardResponse & {
  filename: string;
};

export type GetUsersResponse = StandardResponse & {
  users: AppUserModel[];
};

export type AddUserArgs = StandardRequest & {
  email: string;
  name: string;
  password: string;
  username?: string;
  claimSet: UserClaims[];
};
export type AddUserResponse = StandardResponse & {
  user: AppUserModel;
};

export type AddServiceUserArgs = StandardRequest & {
  email: string;
  name: string;
  password: string;
  claimSet: UserClaims[];
};
export type AddServiceUserResponse = StandardResponse & {
  user: AppUserModel;
};

export type RemoveUserArgs = StandardRequest & {
  email: string;
};
export type RemoveUserResponse = StandardResponse & {
  email: string;
};

export type CreateAppUserRecordRequest = StandardRequest & {
  userId?: string;
};

export interface IGetUserRequest extends StandardRequest {
  userId?: string;
}

export interface IGetUserResponse extends StandardResponse {
  user?: AppUserModel;
  quota?: IUserStorageQuotaSummary;
}

export interface IUpdateUserArgs {
  userId?: string;
  displayName?: string;
  storageQuota?: number;
  claims?: UserClaims;
  active?: boolean;
  disabled?: boolean;
  betaGroup?: string;
}

/**
 * Interface representing a single log entry from the frontend SPA.
 */
export interface ILogSpaEntry {
  timestamp: number;
  level: "log" | "info" | "warn" | "error";
  message: string;
  stack?: string;
}

/**
 * Interface representing the arguments sent to the logSpaBatch function.
 */
export interface ILogSpaBatchArgs {
  logs: ILogSpaEntry[];
  deviceInfo?: {
    os?: string;
    osVersion?: string;
    appVersion?: string;
  };
}

/**
 * Interface representing the request payload to log in with username or email and password.
 */
export interface ILoginWithCredentialsRequest {
  identifier: string;
  password: string;
}

/**
 * Interface representing the response payload from loginWithCredentials.
 */
export interface ILoginWithCredentialsResponse {
  status: boolean;
  token?: string;
  error?: string;
  email?: string;
  authUid?: string;
}

